import debugCreator from 'debug';
import React, { Component } from 'react';

import { Success, Close } from 'components/Shared/Icons/Icons';
import { colors, breakpoints, fontSize } from 'styles/cp';

interface ToasterProps {
  hide: Function;
  status?: string;
  text: string;
}
interface ToasterState {
  shouldClose: boolean;
  isClosed: boolean;
}

const debug = debugCreator('Toastr');

export default class Toastr extends Component<ToasterProps, ToasterState> {
  private timerId?: ReturnType<typeof setTimeout>;
  static TOASTR_TTL = 4000;

  constructor(props: ToasterProps) {
    super(props);

    debug('constructor');

    this.state = {
      shouldClose: true,
      isClosed: true,
    };
  }

  shouldComponentUpdate(nextProps: ToasterProps, nextState: ToasterState) {
    if (nextState !== this.state) {
      return true;
    }

    return this.props.text !== nextProps.text || this.props.status !== nextProps.status;
  }

  componentDidMount() {
    this.setState(
      {
        isClosed: false,
      },
      this.restartTimer
    );
  }

  componentDidUpdate(prevProps: ToasterProps) {
    if (this.props.text !== prevProps.text) {
      this.setState(
        {
          shouldClose: false,
          isClosed: false,
        },
        this.restartTimer
      );
    }
  }

  restartTimer = () => {
    if (!this.state.isClosed) {
      this.timerId = setTimeout(() => {
        this.setState(
          {
            shouldClose: true,
          },
          this.triggerHide
        );
      }, Toastr.TOASTR_TTL);
    }
  };

  stopTimer = () => {
    this.setState(
      {
        shouldClose: false,
      },
      this.clearTimer
    );
  };

  clearTimer = () => (this.timerId ? clearTimeout(this.timerId) : null);

  triggerHide = () => {
    const { shouldClose } = this.state;

    if (shouldClose) {
      this.clearTimer();
      this.setState(
        {
          isClosed: true,
        },
        () => {
          setTimeout(() => {
            this.props.hide();
          }, 500);
        }
      );
    }
  };

  onMouseEnter = () => {
    this.stopTimer();
  };

  onMouseLeave = () => {
    this.restartTimer();
  };

  onCloseToastr = () => {
    this.setState({ shouldClose: true }, this.triggerHide);
  };

  render() {
    const { status = 'success', text } = this.props;
    const icon = status === 'success' ? <Success /> : null;

    return (
      <div className={`toast-wrapper ${this.state.isClosed ? 'closed' : 'open'}`}>
        <div className="toast" onMouseEnter={this.onMouseEnter} onMouseLeave={this.onMouseLeave}>
          <div className="toast-content">
            {icon && <div className="toast-icon">{icon}</div>}
            <span className="toast-text" dangerouslySetInnerHTML={{ __html: text }} />
          </div>
          <span className="toast-close" onClick={this.onCloseToastr}>
            <Close />
          </span>
        </div>

        <style jsx global>{`
          .toast-wrapper {
            position: fixed;
            transform: translateZ(0) translate(-50%, 0);
            left: 50%;
            z-index: 9999;

            &.open {
              top: 16px;
              visibility: visible;
              animation: fadein 0.3s ease-out;
            }

            &.closed {
              top: -70px;
              animation: fadeout 0.3s ease-in;
            }

            @media screen and (max-width: ${breakpoints.md}) {
              transform: translate(0);
              right: 16px;
              left: 16px;
              width: auto;
            }

            @keyframes fadein {
              from {
                top: -70px;
                opacity: 0;
              }
              to {
                top: 16px;
                opacity: 1;
              }
            }

            @keyframes fadeout {
              from {
                top: 16px;
                opacity: 1;
              }
              to {
                top: -70px;
                opacity: 0;
              }
            }

            .toast {
              width: 460px;
              height: 56px;
              background-color: ${colors.white};
              border-radius: 4px;
              border: 1px solid ${colors.gray04};
              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
              opacity: 1;
              display: flex;
              align-items: center;
              justify-content: space-between;
              padding: 0 16px;
              gap: 10px;

              @media screen and (max-width: ${breakpoints.md}) {
                width: auto;
                min-width: 280px;
                max-width: calc(100vw - 32px);
                height: auto;
                min-height: 48px;
              }

              .toast-content {
                display: flex;
                align-items: center;
                gap: 10px;

                .toast-icon {
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  flex-shrink: 0;

                  svg {
                    width: 20px;
                    height: 20px;
                  }
                }

                .toast-text {
                  font-family: AvenirNextforINTUIT-Regular;
                  font-size: ${fontSize.xs};
                  color: ${colors.darkGray};
                  line-height: 1.4;
                }
              }

              .toast-close {
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
                color: ${colors.gray02};
                transition: color 0.2s ease;
                flex-shrink: 0;

                &:hover {
                  color: ${colors.darkGray};
                }

                svg {
                  width: 12px;
                  height: 12px;
                }
              }
            }
          }
        `}</style>
      </div>
    );
  }
}
